/**
 * The view types: a small, view-shaped project model that Chat, Areas, Graph and Needs-you render. The live
 * `ProjectState` (packages/protocol, folded by packages/core) is mapped onto these by `model/view.ts`, the adapter
 * boundary (decision 0017). Fields marked "derived" come from core functions (`readiness`, `agentPresence`); the
 * views never compute them.
 */

import type { AgentKind } from "@minga/protocol"
import type { RunUsageView } from "@/features/usage/usage-text"

export type Tone = "teal" | "green" | "blue" | "purple" | "amber" | "neutral"
export const TONES: ReadonlyArray<Tone> = ["teal", "green", "blue", "purple", "amber", "neutral"]

export interface Area {
  readonly id: string
  readonly name: string
  readonly tone: Tone
  /** charter.mission one-liner */
  readonly mission: string
  /** Plannable top-level items in Now / the area's limit (`wip.maxNow`). */
  readonly wip: { readonly active: number; readonly max: number }
  /** The area's agent: owns the area's new items by default. */
  readonly agentId: string | null
  /** Short KPI line shown under the roster, e.g. "trials 13 users". */
  readonly kpi: string | null
  /** Part of an open plan_change proposal (a ghost on the canvas). */
  readonly ghost: boolean
}

/** metric: a KPI target; judged: a person judges it (target null, with criteria). */
export type GoalKind = "metric" | "judged"

export interface Goal {
  readonly id: string
  readonly ref: string
  readonly kind: GoalKind
  readonly title: string
  readonly why: string
  /** metric: latest KPI value / target; judged: top-level items serving it done / total */
  readonly current: number
  readonly target: number
  readonly delta: string | null
  readonly due: string | null
  readonly deadline: number | null
  /** "paid_users ≥ 100", or "judged by a person". */
  readonly targetText: string
  /** What a person checks when judging the goal. */
  readonly criteria: ReadonlyArray<string>
  readonly kpiIds: ReadonlyArray<string>
  readonly contributingAreaIds: ReadonlyArray<string>
  readonly status: "active" | "achieved" | "missed" | "abandoned"
  readonly ghost: boolean
}

/** An item's status (decision 0035): one status set for every item. */
export type ItemStatus =
  | "triage"
  | "backlog"
  | "todo"
  | "in_progress"
  | "in_review"
  | "blocked"
  | "done"
  | "cancelled"

/**
 * derived (core `readiness` plus the item's review stage): why an item is or isn't moving.
 * running: a run is leased · waiting_for_runner: requested or waiting, with the reasons in `readinessText` ·
 * validating: in review at the validator stage · gate: needs a person (human review, an escalation) ·
 * needs_approval: an open proposal blocks it · person: a person's item, their move ("your turn") ·
 * container: has open children or is ready to close · planning: triage, backlog, an empty plan, a series ·
 * blocked: blocked, reported by its run · waiting: dependencies, not Now, no owner, no checks, backoff ·
 * paused: held · closed: done or cancelled.
 */
export type Readiness =
  | "ready"
  | "running"
  | "validating"
  | "blocked"
  | "waiting_for_runner"
  | "needs_approval"
  | "gate"
  | "person"
  | "container"
  | "planning"
  | "waiting"
  | "paused"
  | "closed"

export interface DoneCheck {
  readonly label: string
  readonly passed: boolean | null
}

export type RunStatus = "waiting" | "live" | "completed" | "failed"

export interface RunRef {
  readonly id: string
  readonly ref: string
  readonly agentId: string | null
  readonly kind: "turn" | "worker" | "validator" | "publish"
  readonly attempt: number
  readonly driver: AgentKind | null
  readonly status: RunStatus
  /** "waiting for runner: jannik-mbp is offline", "preparing", "awaiting permission", "completed" */
  readonly stateText: string
  /** "on jannik-mbp · claude-personal", "platform · anthropic", or null before the lease */
  readonly executorText: string | null
  readonly stopReason: string | null
  readonly requestedAt: number
  readonly endedAt: number | null
}

/**
 * One run as every surface shows it (spec §7.2, visibility §3.3). Built by `toRunSummary` in view.ts from the durable
 * `Run`, the ephemeral `RunActivity` and the names the web knows. The state → phase mapping table lives on
 * `toRunSummary`.
 */
export type RunPhaseView =
  | "queued"
  | "waiting_for_runner"
  | "preparing"
  | "running"
  | "validating"
  | "awaiting_permission"
  | "finishing"
  | "blocked"
  | "question"
  | "done"
  | "failed"
  | "stopped"

export type RunDot = "running" | "waiting" | "blocked" | "gate" | "done" | "idle"
export type RunTone = "neutral" | "outline" | "running" | "gate" | "ok" | "blocked"

export interface RunSummary {
  readonly id: string
  readonly ref: string
  /** Workers and validators: the temporary teammate ("W-3", "V-1"); null for turns and older runs. */
  readonly workerRef: string | null
  readonly kind: "turn" | "worker" | "validator" | "publish"
  readonly attempt: number
  readonly item: {
    readonly id: string
    readonly ref: string
    readonly title: string
    readonly areaId: string | null
  } | null
  /** The supervisor (worker/validator: the item's owner agent) or the agent (turn). */
  readonly agent: {
    readonly id: string
    readonly name: string
    readonly handle: string
    readonly tone: Tone
  } | null
  /** The worker preset's key the run froze (`Run.worker.preset`). */
  readonly preset: string | null
  readonly driver: AgentKind | null
  /** Runner NAME (never the raw id unless unknown). */
  readonly runner: string | null
  readonly runnerId: string | null
  readonly profile: string | null
  /** activity.model ?? usage[0].model ?? hands.model ?? null ("CLI default"). */
  readonly model: string | null
  readonly phase: RunPhaseView
  /** "waiting for runner: jannik-mbp is offline", "blocked: missing access: PostHog" */
  readonly phaseText: string
  readonly dot: RunDot
  /** Pulse ring: running or validating. */
  readonly pulse: boolean
  readonly tone: RunTone
  /** Start of the current phase. */
  readonly since: number
  readonly requestedAt: number
  readonly startedAt: number | null
  readonly endedAt: number | null
  /** Not ended. */
  readonly live: boolean
  /** Running and quiet for 5 min or more (a hint only). */
  readonly stalled: boolean
  readonly idleMs: number | null
  /** "Bash · pnpm test", "“Opening the PR now…”" */
  readonly last: { readonly text: string; readonly tool: string | null; readonly at: number } | null
  readonly pr: PrLinkView | null
  /** The run's branch; null = scratch dir or not known. */
  readonly branch: string | null
  /** True when the run is known to work in a scratch dir (no repo). */
  readonly scratch: boolean
  /**
   * Wave F (decision 0062): fresh tokens so far (live, no money) or the ended run's chip, detail, asked-vs-paid line and
   * per-model rows. Null without usage.
   */
  readonly usage: RunUsageView | null
  /** False for a run that never started (and for publish runs). */
  readonly transcript: boolean
  readonly stopReason: string | null
}

/** A PR link as shown (see `PrLink` in view.ts). */
export interface PrLinkView {
  readonly url: string
  readonly label: string
  readonly repo: string | null
  readonly verified: boolean
}

export interface Verdict {
  readonly result: "pass" | "fail" | "inconclusive"
  readonly text: string
  /** Decision 0147: a pass's minor findings (the item passed; its owner decides about follow-ups). */
  readonly notes?: ReadonlyArray<string>
}

/** Who answers for an item (its owner), or does it (a person assignee), as the views show them. */
export type PersonOrAgent =
  | {
      readonly kind: "person"
      readonly id: string
      readonly name: string
      readonly initials: string
      readonly tone: Tone
      /** The signed-in person. */
      readonly me: boolean
    }
  | {
      readonly kind: "agent"
      readonly id: string
      readonly name: string
      readonly handle: string
      readonly tone: Tone
    }

/** The Assignee line (core `assigneeLine`): who does the item. */
export type AssigneeView =
  | { readonly kind: "person"; readonly who: PersonOrAgent }
  | { readonly kind: "worker"; readonly agentId: string | null; readonly preset: string | null }
  | { readonly kind: "agent_itself"; readonly agentId: string }
  /** Plannable items (their children do the work) and items in triage. */
  | { readonly kind: "none" }
  /** A leaf that should have an assignee and has none. */
  | { readonly kind: "nobody" }

/** One-click fixes next to a readiness line (§1.5). */
export type ReadinessFix =
  | { readonly kind: "run_now" }
  | { readonly kind: "resume" }
  | { readonly kind: "close" }
  | { readonly kind: "add_item" }
  | { readonly kind: "assign"; readonly missing: "owner" | "assignee" }
  | { readonly kind: "add_checks" }
  | { readonly kind: "start_top"; readonly itemId: string; readonly label: string }
  | { readonly kind: "start_now" }
  | { readonly kind: "remove_dependency"; readonly itemIds: ReadonlyArray<string> }
  | { readonly kind: "open_card"; readonly proposalId: string }
  | { readonly kind: "mark_done" }
  | { readonly kind: "open_runners" }

/** Progress over an item's descendant leaves (core `progressOf`), cancelled left out. */
export interface ProgressView {
  readonly done: number
  readonly inFlight: number
  readonly blocked: number
  readonly todo: number
  readonly total: number
}

/**
 * One item (decision 0035): anything that can be finished. One noun for what was an initiative, a task or an intake:
 * a top-level item has no parent; a container has children; a leaf is done by its assignee.
 */
export interface Item {
  readonly id: string
  readonly ref: string
  /** A key of the type registry ("release", "bug", …). */
  readonly type: string
  /** "Release", "Bug", … */
  readonly typeLabel: string
  readonly title: string
  readonly why: string
  readonly areaId: string | null
  readonly parentId: string | null
  /** The top-level item of its tree (itself when top-level). */
  readonly topId: string
  /** 0 for a top-level item. */
  readonly depth: number
  /** The top-level item's goals (children inherit them). */
  readonly goalIds: ReadonlyArray<string>
  readonly status: ItemStatus
  /** Planning / Ready / Now for planning items, else Planning / Ready / In progress, … */
  readonly statusLabel: string
  /** A top-level plannable item or a top-level container: its statuses read Later / Next / Now. */
  readonly planning: boolean
  readonly plannable: boolean
  /** Has at least one child that is not cancelled. */
  readonly container: boolean
  /** A series (recurrence set): Run now creates an occurrence. */
  readonly recurring: boolean
  /** derived */
  readonly readiness: Readiness
  /** core readiness tag (for fixes and filters). */
  readonly readinessTag: string
  /** derived: the reason line for `readiness` ("waiting for runner: no runner online", "waiting on Jannik · DIC-3") */
  readonly readinessText: string | null
  readonly fix: ReadinessFix | null
  readonly owner: PersonOrAgent | null
  readonly assignee: AssigneeView
  /** A person's move now (readiness `with_person`): the amber frame. */
  readonly personsMove: string | null
  /** Open leaves that wait on this item ("holds n"). */
  readonly holds: number
  /** "waiting on Jannik · DIC-3" when a person's item holds it (amber text), else null. */
  readonly waitingOnPerson: string | null
  /** Containers: progress over descendant leaves; null for leaves. */
  readonly progress: ProgressView | null
  readonly dueAt: number | null
  readonly overdue: boolean
  /** The live or last run's executor: "on jannik-mbp · claude-personal" */
  readonly placement: string | null
  /** Short label of the work product, e.g. "PR #12". */
  readonly artifact: string | null
  readonly prUrl: string | null
  readonly verdict: Verdict | null
  readonly checks: ReadonlyArray<DoneCheck>
  readonly runs: ReadonlyArray<RunRef>
  readonly paused: boolean
  /** Why it is blocked (core `blocked._tag`); null unless `status` is `blocked`. */
  readonly blocked: "needs_human" | "question" | "reported" | "impossible_claimed" | null
  /** Who paused it (decision 0032): "you", the person's name, "@lea"; null when not paused or unknown. */
  readonly pausedBy: string | null
  /** "won't do", "duplicate of DIC-3", "impossible", "superseded by DIC-9"; null unless cancelled. */
  readonly resolution: string | null
  /** Decision 0101: what a done item delivered ("report delivered", "no data", "verified"); null otherwise. */
  readonly result: string | null
  readonly attempts: number
  /** Captured from outside: shown and packed as quoted data. */
  readonly tainted: boolean
  /** "web", "agent", "email", …; null when planned directly. */
  readonly source: string | null
  readonly kind: "work" | "publish"
  readonly rank: string
  readonly tags: ReadonlyArray<string>
  readonly createdAt: number
  readonly updatedAt: number
  readonly closedAt: number | null
  readonly ghost: boolean
}

export type Presence = "online" | "working" | "waiting" | "away"

export interface Agent {
  readonly id: string
  readonly name: string
  readonly handle: string
  readonly role: "lead" | "member"
  readonly title: string
  readonly areaId: string | null
  readonly tone: Tone
  readonly mind: "cloud" | "runner"
  /** runner name (or id while unknown) of a runner mind */
  readonly runner: string | null
  readonly driver: AgentKind | null
  /** "jannik-mbp · claude-personal", "In the cloud (Anthropic)" */
  readonly mindLabel: string
  readonly presence: Presence
  /** derived: core agentPresence state and reason */
  readonly state: "idle" | "queued" | "thinking" | "working" | "away" | "paused" | "needs_attention"
  readonly reason: string | null
  /** what the agent is doing right now, e.g. "thinking", "away · jannik-mbp offline" */
  readonly activity: string
  /** Live workers and validators it supervises ("supervising n"); never its own activity (K16). */
  readonly supervising: number
  readonly paused: boolean
  readonly ghost: boolean
}

export type EdgeKind = "parent" | "blocks" | "informs"

export interface Edge {
  readonly id: string
  readonly kind: EdgeKind
  /** child for parent edges, predecessor for blocks, producer for informs */
  readonly from: string
  readonly to: string
  readonly interfaceKey?: string
  readonly ghost: boolean
}

export interface Member {
  readonly id: string
  readonly name: string
  readonly initials: string
  readonly tone: Tone
}

/** The whole project as the views see it. */
export interface ProjectModel {
  readonly id: string
  readonly name: string
  readonly description: string
  readonly leadAgentId: string | null
  readonly goals: ReadonlyArray<Goal>
  readonly areas: ReadonlyArray<Area>
  readonly agents: ReadonlyArray<Agent>
  /** Every item, closed ones included (they are part of the plan), in rank order. */
  readonly items: ReadonlyArray<Item>
  readonly edges: ReadonlyArray<Edge>
  readonly members: ReadonlyArray<Member>
}

/**
 * A temporary teammate (settings wave §3.7, decision 0025): one worker or validator run, shown as its own teammate
 * with a supervisor, never as the supervisor's own activity. Built by `toTeammate` in view.ts from `RunSummary` and
 * `Run.worker`.
 */
export interface Teammate {
  readonly runId: string
  /** "W-3" / "V-1"; fallback for runs without `worker`: the run ref "R-12". */
  readonly ref: string
  readonly kind: "worker" | "validator"
  /** "W-3 · Codex", "V-1 · Claude reviewer"; "W-3" while the CLI is unknown. */
  readonly name: string
  readonly item: {
    readonly id: string
    readonly ref: string
    readonly title: string
    readonly round: number | null
  } | null
  readonly supervisor: { readonly id: string; readonly name: string; readonly handle: string } | null
  /** The preset's label. */
  readonly preset: string | null
  /** "jannik-mbp · codex-personal" */
  readonly where: string | null
  readonly model: string | null
  readonly state: TeammateState
  /** "waiting for jannik-mbp", "working · Bash · pnpm test", "done → in review" */
  readonly stateText: string
  readonly since: number
  readonly endedAt: number | null
  readonly live: boolean
  readonly driver: AgentKind | null
}

export type TeammateState =
  | "spawning"
  | "preparing"
  | "working"
  | "awaiting_you"
  | "finishing"
  | "done"
  | "blocked"
  | "question"
  | "impossible"
  | "failed"
  | "stopped"
